import axios from 'axios';

import {
    PATH_AFTER_LOGIN,
    PATH_AFTER_LOGIN_OPERATOR,
    PATH_AFTER_LOGIN_INSURER,
    PATH_AFTER_LOGIN_BANKER,
    PATH_AFTER_LOGIN_SUBADMIN,
    HOST_API
} from '../config';

export const getDefaultRouteFromUserType = (userType) => {
    if (!userType) return '/';

    let parsedUserType = userType;
    if (typeof userType === 'string') {
        try {
            parsedUserType = JSON.parse(userType);
        } catch (_) {
            return '/';
        }
    }

    if (parsedUserType?.isSuperAdmin) return PATH_AFTER_LOGIN;
    if (parsedUserType?.isAdmin) return PATH_AFTER_LOGIN_INSURER;
    if (parsedUserType?.isBroker) return PATH_AFTER_LOGIN_OPERATOR;
    if (parsedUserType?.isClient) return PATH_AFTER_LOGIN_BANKER;
    if (parsedUserType?.isSubAdmin) return PATH_AFTER_LOGIN_SUBADMIN;

    return '/';
};

// API service for user redirect functionality
export const userRedirectService = {
    // Generate login token for user redirect
    generateLoginToken: async (userId) => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            if (!accessToken) {
                throw new Error('No access token found');
            }

            const response = await axios.post(
                `${HOST_API}/user/generate-login-token`,
                {
                    userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            return response.data;
        } catch (error) {
            // Log error for debugging but don't expose sensitive details
            console.error('Failed to generate login token');
            throw error;
        }
    },

    // Generate admin login token for sub-admin redirect
    generateSubAdminAdminLoginToken: async (adminId) => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            if (!accessToken) {
                throw new Error('No access token found');
            }

            const response = await axios.post(
                `${HOST_API}/user/sub-admin/generate-admin-login-token`,
                {
                    adminId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error('Failed to generate sub-admin admin login token');
            throw error;
        }
    },

    // Store current token and switch to new user
    switchToUser: async (userId) => {
        try {
            // Step 1: Get current access token and userType
            const currentAccessToken = localStorage.getItem('accessToken');
            const currentUserType = localStorage.getItem('userType');

            if (!currentAccessToken) {
                throw new Error('No current access token found');
            }

            // Step 2: Generate new login token
            const response = await userRedirectService.generateLoginToken(userId);

            if (!response.token) {
                throw new Error('No token received from API');
            }

            // Step 3: Store current token and userType as parent data
            localStorage.setItem('parentAccessToken', currentAccessToken);
            if (currentUserType) {
                localStorage.setItem('parentUserType', currentUserType);
            }

            // Step 4: Set new token as current access token
            localStorage.setItem('accessToken', response.token);

            // Step 5: Store user information for reference
            if (response.user) {
                localStorage.setItem('redirectedUserInfo', JSON.stringify(response.user));
            }

            // Step 6: Update userType to child user's type if available
            if (response.userType) {
                localStorage.setItem('userType', JSON.stringify(response.userType));
            }

            // Step 7: Mark that we are in redirected mode
            localStorage.setItem('isRedirectedUser', 'true');

            return response;
        } catch (error) {
            console.error('Failed to switch user');
            throw error;
        }
    },

    // Go back to parent/original user
    switchBackToParent: () => {
        try {
            // Step 1: Get parent access token and userType
            const parentAccessToken = localStorage.getItem('parentAccessToken');
            const parentUserType = localStorage.getItem('parentUserType');

            if (!parentAccessToken) {
                throw new Error('No parent access token found');
            }

            // Step 2: Restore parent token as current access token
            localStorage.setItem('accessToken', parentAccessToken);

            // Step 3: Restore parent userType
            if (parentUserType) {
                localStorage.setItem('userType', parentUserType);
            }

            // Step 4: Clean up redirect-related data
            localStorage.removeItem('parentAccessToken');
            localStorage.removeItem('parentUserType');
            localStorage.removeItem('redirectedUserInfo');
            localStorage.removeItem('redirectedAdminAssignment');
            localStorage.removeItem('redirectSourceRole');
            localStorage.removeItem('isRedirectedUser');

            return true;
        } catch (error) {
            console.error('Failed to switch back to parent');
            throw error;
        }
    },

    // Sub-admin redirect to admin
    switchSubAdminToAdmin: async (adminId) => {
        try {
            const currentAccessToken = localStorage.getItem('accessToken');
            const currentUserType = localStorage.getItem('userType');

            if (!currentAccessToken) {
                throw new Error('No current access token found');
            }

            const response = await userRedirectService.generateSubAdminAdminLoginToken(adminId);

            if (!response.token) {
                throw new Error('No token received from API');
            }

            localStorage.setItem('parentAccessToken', currentAccessToken);
            if (currentUserType) {
                localStorage.setItem('parentUserType', currentUserType);
            }

            localStorage.setItem('accessToken', response.token);

            if (response.targetUser) {
                localStorage.setItem('redirectedUserInfo', JSON.stringify(response.targetUser));
            }

            if (response.adminAssignment) {
                localStorage.setItem('redirectedAdminAssignment', JSON.stringify(response.adminAssignment));
            } else {
                localStorage.removeItem('redirectedAdminAssignment');
            }

            localStorage.setItem('redirectSourceRole', 'subAdmin');
            localStorage.setItem('isRedirectedUser', 'true');

            return response;
        } catch (error) {
            console.error('Failed to switch sub-admin to admin');
            throw error;
        }
    },

    // Check if currently in redirected mode
    isInRedirectMode: () => {
        return localStorage.getItem('isRedirectedUser') === 'true';
    },

    // Get redirected user information
    getRedirectedUserInfo: () => {
        const userInfo = localStorage.getItem('redirectedUserInfo');
        return userInfo ? JSON.parse(userInfo) : null;
    },

    // Clear all redirect data (for logout)
    clearRedirectData: () => {
        localStorage.removeItem('parentAccessToken');
        localStorage.removeItem('parentUserType');
        localStorage.removeItem('redirectedUserInfo');
        localStorage.removeItem('redirectedAdminAssignment');
        localStorage.removeItem('redirectSourceRole');
        localStorage.removeItem('isRedirectedUser');
    }
};